<template>
    <SmuDialog v-if="task" has-close-button :open="true" @close="close">
        <template #title>
            {{ store.taskLabel(task) }}
        </template>

        <div
            class="flex w-[60rem] max-w-full flex-col gap-y-4 max-sm:w-full"
            data-testid="vectorize-live-dialog"
        >
            <!-- Wartet noch: Platz in der Schlange statt einer leeren Bühne -->
            <div
                v-if="task.status === 'queued'"
                class="bg-neutral-10/60 flex items-center gap-3 rounded-lg px-3 py-4"
            >
                <SmuIcon icon="hourglass-half" type="light" class="text-lg" />
                <div class="flex-1">
                    <p class="font-medium">
                        {{
                            t("vectorize.queue.waiting_position", {
                                position: (task.position ?? 0) + 1,
                            })
                        }}
                    </p>
                    <p class="text-neutral-5 text-xs">
                        {{ t("vectorize.queue.waiting_hint") }}
                    </p>
                </div>
                <SmuButton
                    v-if="(task.position ?? 0) > 0"
                    size="sm"
                    variant="outline"
                    @click="store.reorder(frontOrder(task.id))"
                >
                    {{ t("vectorize.queue.move_to_front") }}
                </SmuButton>
                <SmuButton
                    size="sm"
                    variant="outline"
                    color="secondary"
                    @click="removeAndClose(task.id)"
                >
                    {{ t("vectorize.queue.remove") }}
                </SmuButton>
            </div>

            <VectorizePlayer v-else :playback="playback" />

            <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-neutral-5 text-xs">
                    {{
                        task.status === "running"
                            ? t("vectorize.queue.running_hint")
                            : ""
                    }}
                </span>
                <div class="flex flex-wrap justify-end gap-2">
                    <VectorizeQueueCancelButton
                        v-if="task.status === 'running'"
                        :task-id="task.id"
                    />
                    <SmuButton
                        v-if="
                            task.status === 'failed' ||
                            task.status === 'cancelled'
                        "
                        size="sm"
                        variant="outline"
                        @click="store.retry(task.id)"
                    >
                        {{ t("helpers.buttons.vectorize_retry") }}
                    </SmuButton>
                    <SmuButton
                        v-if="target"
                        size="sm"
                        data-testid="vectorize-live-open"
                        @click="openTarget"
                    >
                        {{
                            task.meta.origin === "logo"
                                ? t("vectorize.queue.open_logo")
                                : t("vectorize.queue.open_vectorizer")
                        }}
                    </SmuButton>
                </div>
            </div>
        </div>
    </SmuDialog>
</template>

<script setup lang="ts">
import { SmuButton, SmuDialog, SmuIcon } from "@smakecloud/smake-ui";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useVectorizeTaskPlayback } from "~/composables/useVectorizeTaskPlayback";

const { t } = useI18n();
const store = useVectorizeTaskStore();
const { slug } = storeToRefs(useSlugStore());
const router = useRouter();
const { follow, playback, stop } = useVectorizeTaskPlayback();

const task = computed(() => store.taskById(store.watchedTaskId));

/** Where the task belongs: its logo, or the standalone vectorizer. */
const target = computed(() => {
    const current = task.value;

    if (!current || !slug.value) return;

    const query = { vectorize: current.id };

    if (current.meta.origin === "vectorize-page") {
        return {
            name: "slug-vectorize" as const,
            params: { slug: slug.value },
            query,
        };
    }

    const logoId = current.meta.logoId;

    if (logoId === undefined) return;

    return current.meta.customerId === undefined
        ? {
              name: "slug-logos-id" as const,
              params: { id: logoId, slug: slug.value },
              query,
          }
        : {
              name: "slug-customers-customer-logos-id" as const,
              params: {
                  id: logoId,
                  customer: current.meta.customerId,
                  slug: slug.value,
              },
              query,
          };
});

// Follows the task while it is shown; a waiting task starts on its own.
watch(
    () => [task.value?.id, task.value?.status === "queued"] as const,
    ([id, waiting]) => {
        if (id && !waiting) {
            void follow(id);
        } else {
            stop();
        }
    },
    { immediate: true },
);

function close() {
    store.closeLive();
}

function frontOrder(id: string): string[] {
    return [
        id,
        ...store.queued
            .map((queued) => queued.id)
            .filter((queued) => queued !== id),
    ];
}

async function openTarget() {
    const destination = target.value;

    if (!destination) return;

    close();
    store.panelOpen = false;
    await router.push(destination);
}

async function removeAndClose(id: string) {
    close();
    await store.remove(id);
}
</script>
